import type { Schemas } from '@ers/sdk';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call, ifMatch } from '../../api/client';
import type { ApiBody, ApiQuery } from '../../components/data/apiTypes';
import { useCursorQuery } from '../../components/data/useCursorQuery';
import { uploadBinary, type UploadProgress } from '../../components/data/upload';
import { useRouteProbe } from '../../components/data/useRouteProbe';
import { useSession } from '../../app/session';

/*
 * Відеозустрічі (модуль meetings, М20): зустріч із задачі, справи чи координації, учасники й гості, згода на запис,
 * вхід до кімнати Jitsi за посиланням із ЄРС, записи й розшифровки, звіт ШІ-12 «Протокол зустрічі» з погодженням
 * ведучим, чернетки задач із доручень. Типи — з контракту (@ers/sdk); ключі кешу — під префіксом ['meetings'].
 */

export type Meeting = Schemas['Meeting'];
export type MeetingCreated = Schemas['MeetingCreated'];
export type MeetingType = Meeting['type'];
export type MeetingStatus = Meeting['status'];
export type MeetingLink = NonNullable<Meeting['link']>;
export type MeetingLinkKind = MeetingLink['kind'];
export type MeetingGuest = Schemas['MeetingGuest'];
export type MeetingGuestLink = Schemas['MeetingGuestLink'];
export type MeetingJoin = Schemas['MeetingJoin'];
export type MeetingRecording = Schemas['MeetingRecording'];
export type MeetingTranscript = Schemas['MeetingTranscript'];
export type MeetingReport = Schemas['MeetingReport'];
export type MeetingReportSection = MeetingReport['content']['sections'][number];
export type MeetingReportTask = MeetingReport['content']['tasks'][number];
export type MeetingTaskDraft = Schemas['MeetingTaskDraft'];
export type MeetingSettings = Schemas['MeetingSettings'];
export type MeetingSettingsUpdated = Schemas['MeetingSettingsUpdated'];
export type MeetingReportTemplate = Schemas['MeetingReportTemplate'];
export type MeetingTemplateField = MeetingReportTemplate['fields'][number];
export type MeetingDeletion = Schemas['MeetingDeletion'];
export type MeetingRetentionResult = Schemas['MeetingRetentionResult'];

export type MeetingListQuery = ApiQuery<'/api/v1/meetings', 'get'>;
export type MeetingCreateBody = ApiBody<'/api/v1/meetings', 'post'>;
export type MeetingPatchBody = ApiBody<'/api/v1/meetings/{id}', 'patch'>;
export type MeetingSettingsPatch = ApiBody<'/api/v1/meetings/settings', 'patch'>;
export type MeetingTemplateUpdate = ApiBody<'/api/v1/meetings/report-templates/{type}', 'put'>;
export type MeetingReportPatch = ApiBody<'/api/v1/meetings/{id}/report', 'patch'>;
export type MeetingReportApprove = ApiBody<'/api/v1/meetings/{id}/report/approve', 'post'>;
export type MeetingDraftNew = ApiBody<'/api/v1/meetings/{id}/task-drafts', 'post'>;
export type MeetingDraftPatch = ApiBody<'/api/v1/meetings/{id}/task-drafts/{draftId}', 'patch'>;
export type MeetingDraftsConfirm = ApiBody<'/api/v1/meetings/{id}/task-drafts/confirm', 'post'>;

/** Ключі кешу: усе під ['meetings'] — подія потоку «meetings.*» оновлює модуль цілком. */
export const meetingKeys = {
  all: ['meetings'] as const,
  probe: ['probe', 'meetings'] as const,
  list: (q: Record<string, unknown>) => ['meetings', 'list', q] as const,
  byLink: (kind: string, id: string) => ['meetings', 'link', kind, id] as const,
  one: (id: string) => ['meetings', 'one', id] as const,
  guestLinks: (id: string) => ['meetings', 'one', id, 'guest-links'] as const,
  recordings: (id: string) => ['meetings', 'one', id, 'recordings'] as const,
  transcripts: (id: string) => ['meetings', 'one', id, 'transcripts'] as const,
  transcript: (id: string, tid: string) => ['meetings', 'one', id, 'transcripts', tid] as const,
  report: (id: string) => ['meetings', 'one', id, 'report'] as const,
  drafts: (id: string) => ['meetings', 'one', id, 'drafts'] as const,
  settings: ['meetings', 'settings'] as const,
  templates: ['meetings', 'templates'] as const,
  deletions: ['meetings', 'deletions'] as const,
};

/** Модуль підключено й доступний (для реєстру модулів). */
export function useMeetingsAvailable(): boolean {
  const { can } = useSession();
  return useRouteProbe(meetingKeys.probe, '/api/v1/meetings?limit=1', { enabled: can('tasks:read') }).available;
}

// ——— Зустрічі ———

export function useMeetings(q: MeetingListQuery, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: meetingKeys.list(q as Record<string, unknown>),
    queryFn: async () => call(api.GET('/api/v1/meetings', { params: { query: q } })),
    enabled: opts.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Минулі зустрічі з курсорною пагінацією («Показати ще»). */
export function useMeetingsPaged(q: Omit<MeetingListQuery, 'cursor'>, opts: { enabled?: boolean } = {}) {
  return useCursorQuery<Meeting>(
    meetingKeys.list({ ...q, paged: true }),
    async (cursor) => call(api.GET('/api/v1/meetings', { params: { query: { ...q, cursor } } })),
    { enabled: opts.enabled },
  );
}

/** Зустрічі пов’язаного об’єкта (задачі, замовлення, управлінської одиниці, проєкту). */
export function useLinkedMeetings(kind: MeetingLinkKind, id: string, enabled = true) {
  return useQuery({
    queryKey: meetingKeys.byLink(kind, id),
    queryFn: async () => call(api.GET('/api/v1/meetings', { params: { query: { linkKind: kind, linkId: id, limit: 20 } } })),
    enabled,
    staleTime: 30_000,
  });
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: meetingKeys.one(id ?? ''),
    queryFn: async () => call(api.GET('/api/v1/meetings/{id}', { params: { path: { id: id! } } })),
    enabled: !!id,
    staleTime: 15_000,
  });
}

function useSetMeeting() {
  const qc = useQueryClient();
  return (m: Meeting) => {
    qc.setQueryData(meetingKeys.one(m.id), m);
    void qc.invalidateQueries({ queryKey: ['meetings', 'list'] });
    void qc.invalidateQueries({ queryKey: ['meetings', 'link'] });
  };
}

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MeetingCreateBody) => call(api.POST('/api/v1/meetings', { body })),
    onSuccess: (m) => {
      qc.setQueryData(meetingKeys.one(m.id), m);
      void qc.invalidateQueries({ queryKey: ['meetings', 'list'] });
      void qc.invalidateQueries({ queryKey: ['meetings', 'link'] });
    },
  });
}

/** Зміна зустрічі (If-Match — версія). Для форми нової зустрічі хук викликається без зустрічі й не використовується. */
export function usePatchMeeting(m: Meeting | undefined) {
  const set = useSetMeeting();
  return useMutation({
    mutationFn: (body: MeetingPatchBody) => {
      if (!m) throw new Error('Зустріч не обрано');
      return call(api.PATCH('/api/v1/meetings/{id}', { params: { path: { id: m.id } }, body, headers: ifMatch(m.version) }));
    },
    onSuccess: set,
  });
}

export function useCancelMeeting(id: string) {
  const set = useSetMeeting();
  return useMutation({
    mutationFn: (reason: string) => call(api.POST('/api/v1/meetings/{id}/cancel', { params: { path: { id } }, body: { reason } })),
    onSuccess: set,
  });
}

export function useEndMeeting(id: string) {
  const set = useSetMeeting();
  return useMutation({
    mutationFn: () => call(api.POST('/api/v1/meetings/{id}/end', { params: { path: { id } } })),
    onSuccess: set,
  });
}

/** Посилання для входу працівника (JWT) — запитується в момент натискання «Увійти». */
export async function fetchJoin(id: string): Promise<MeetingJoin> {
  return call(api.GET('/api/v1/meetings/{id}/join', { params: { path: { id } } }));
}

export function useConsent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decision: 'granted' | 'declined') => call(api.POST('/api/v1/meetings/{id}/consent', { params: { path: { id } }, body: { decision } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: meetingKeys.one(id) }),
  });
}

// ——— Гості ———

export function useGuestLinks(id: string, enabled: boolean) {
  return useQuery({
    queryKey: meetingKeys.guestLinks(id),
    queryFn: async () => (await call(api.GET('/api/v1/meetings/{id}/guest-links', { params: { path: { id } } }))).data,
    enabled,
    staleTime: 60_000,
  });
}

export function useAddGuest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; email: string }) => call(api.POST('/api/v1/meetings/{id}/guests', { params: { path: { id } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meetingKeys.one(id) });
    },
  });
}

export function useRemoveGuest(id: string) {
  const set = useSetMeeting();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (guestId: string) => call(api.DELETE('/api/v1/meetings/{id}/guests/{guestId}', { params: { path: { id, guestId } } })),
    onSuccess: (m) => {
      set(m);
      void qc.invalidateQueries({ queryKey: meetingKeys.guestLinks(id) });
    },
  });
}

// ——— Записи й розшифровки ———

export function useRecordings(id: string, enabled: boolean) {
  return useQuery({
    queryKey: meetingKeys.recordings(id),
    queryFn: async () => (await call(api.GET('/api/v1/meetings/{id}/recordings', { params: { path: { id } } }))).data,
    enabled,
    staleTime: 30_000,
  });
}

export function useDeleteRecording(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ recordingId, reason }: { recordingId: string; reason: string }) =>
      call(api.POST('/api/v1/meetings/{id}/recordings/{recordingId}/delete', { params: { path: { id, recordingId } }, body: { reason } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meetingKeys.recordings(id) });
      void qc.invalidateQueries({ queryKey: meetingKeys.transcripts(id) });
    },
  });
}

export type UploadResult = { recording: MeetingRecording; transcriptId: string | null };

/** Завантажити аудіо чи відео для розшифровки: двійкове тіло з поступом (без ключа ідемпотентності). */
export function uploadRecording(
  id: string,
  file: File,
  opts: { consentConfirmed: boolean; transcribe: boolean; onProgress?: (p: UploadProgress) => void; signal?: AbortSignal },
): Promise<UploadResult> {
  const q = new URLSearchParams({ fileName: file.name.slice(0, 200), consentConfirmed: String(opts.consentConfirmed), transcribe: String(opts.transcribe) });
  return uploadBinary<UploadResult>(`/api/v1/meetings/${encodeURIComponent(id)}/uploads?${q.toString()}`, file, {
    contentType: file.type || 'application/octet-stream',
    onProgress: opts.onProgress,
    signal: opts.signal,
  });
}

/** Адреса файла запису для програвача (сеанс — cookie; підтримується Range). */
export function recordingFileUrl(meetingId: string, recordingId: string): string {
  return `/api/v1/meetings/${encodeURIComponent(meetingId)}/recordings/${encodeURIComponent(recordingId)}/file`;
}

export function useTranscripts(id: string, enabled: boolean) {
  return useQuery({
    queryKey: meetingKeys.transcripts(id),
    queryFn: async () => (await call(api.GET('/api/v1/meetings/{id}/transcripts', { params: { path: { id } } }))).data,
    enabled,
    staleTime: 15_000,
    // Поки розшифровка в черзі чи триває — оновлюємо стан.
    refetchInterval: (q) => (q.state.data?.some((t) => t.status === 'queued' || t.status === 'running') ? 5000 : false),
  });
}

export function useTranscript(id: string, transcriptId: string | null) {
  return useQuery({
    queryKey: meetingKeys.transcript(id, transcriptId ?? ''),
    queryFn: async () => call(api.GET('/api/v1/meetings/{id}/transcripts/{transcriptId}', { params: { path: { id, transcriptId: transcriptId! } } })),
    enabled: !!transcriptId,
    staleTime: 5 * 60_000,
  });
}

export function useRequestTranscript(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recordingId: string) => call(api.POST('/api/v1/meetings/{id}/transcripts', { params: { path: { id } }, body: { recordingId } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: meetingKeys.transcripts(id) }),
  });
}

// ——— Звіт і чернетки задач ———

export function useReport(id: string, enabled: boolean) {
  return useQuery({
    queryKey: meetingKeys.report(id),
    queryFn: async () => call(api.GET('/api/v1/meetings/{id}/report', { params: { path: { id } } })),
    enabled,
    staleTime: 15_000,
    retry: (count, e) => !(e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 404) && count < 1,
  });
}

function useReportSetter(id: string) {
  const qc = useQueryClient();
  return (r: MeetingReport) => {
    qc.setQueryData(meetingKeys.report(id), r);
    void qc.invalidateQueries({ queryKey: meetingKeys.one(id) });
    void qc.invalidateQueries({ queryKey: meetingKeys.drafts(id) });
  };
}

export function useGenerateReport(id: string) {
  const set = useReportSetter(id);
  return useMutation({
    mutationFn: (body: ApiBody<'/api/v1/meetings/{id}/report', 'post'>) => call(api.POST('/api/v1/meetings/{id}/report', { params: { path: { id } }, body })),
    onSuccess: set,
  });
}

export function usePatchReport(id: string) {
  const set = useReportSetter(id);
  return useMutation({
    mutationFn: ({ version, body }: { version: number; body: MeetingReportPatch }) =>
      call(api.PATCH('/api/v1/meetings/{id}/report', { params: { path: { id } }, body, headers: ifMatch(version) })),
    onSuccess: set,
  });
}

export function useApproveReport(id: string) {
  const set = useReportSetter(id);
  return useMutation({
    mutationFn: (body: MeetingReportApprove) => call(api.POST('/api/v1/meetings/{id}/report/approve', { params: { path: { id } }, body })),
    onSuccess: set,
  });
}

export function useDrafts(id: string, enabled: boolean) {
  return useQuery({
    queryKey: meetingKeys.drafts(id),
    queryFn: async () => (await call(api.GET('/api/v1/meetings/{id}/task-drafts', { params: { path: { id } } }))).data,
    enabled,
    staleTime: 15_000,
  });
}

export function useAddDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MeetingDraftNew) => call(api.POST('/api/v1/meetings/{id}/task-drafts', { params: { path: { id } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: meetingKeys.drafts(id) }),
  });
}

export function usePatchDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draft, body }: { draft: MeetingTaskDraft; body: MeetingDraftPatch }) =>
      call(api.PATCH('/api/v1/meetings/{id}/task-drafts/{draftId}', { params: { path: { id, draftId: draft.id } }, body, headers: ifMatch(draft.version) })),
    onSuccess: (d) => {
      qc.setQueryData<MeetingTaskDraft[]>(meetingKeys.drafts(id), (list) => list?.map((x) => (x.id === d.id ? d : x)));
      void qc.invalidateQueries({ queryKey: meetingKeys.drafts(id) });
    },
  });
}

export function useConfirmDrafts(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MeetingDraftsConfirm) => call(api.POST('/api/v1/meetings/{id}/task-drafts/confirm', { params: { path: { id } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meetingKeys.drafts(id) });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// ——— Налаштування (адміністратор) ———

export function useMeetingSettings(enabled = true) {
  return useQuery({
    queryKey: meetingKeys.settings,
    queryFn: async () => call(api.GET('/api/v1/meetings/settings')),
    enabled,
    staleTime: 60_000,
  });
}

export function usePatchMeetingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, body }: { version: number; body: MeetingSettingsPatch }) =>
      call(api.PATCH('/api/v1/meetings/settings', { body, headers: ifMatch(version) })),
    onSuccess: (s) => {
      const { generatedSecrets: _secrets, ...rest } = s;
      qc.setQueryData(meetingKeys.settings, rest);
      void qc.invalidateQueries({ queryKey: meetingKeys.settings });
    },
  });
}

export function useReportTemplates(enabled = true) {
  return useQuery({
    queryKey: meetingKeys.templates,
    queryFn: async () => (await call(api.GET('/api/v1/meetings/report-templates'))).data,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, version, body }: { type: MeetingType; version: number | null; body: MeetingTemplateUpdate }) =>
      call(api.PUT('/api/v1/meetings/report-templates/{type}', { params: { path: { type } }, body, headers: version ? ifMatch(version) : undefined })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: meetingKeys.templates }),
  });
}

export function useDeletions(enabled = true) {
  return useCursorQuery<MeetingDeletion>(meetingKeys.deletions, async (cursor) => call(api.GET('/api/v1/meetings/deletions', { params: { query: { cursor, limit: 50 } } })), {
    enabled,
  });
}

export function useRunRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) => call(api.POST('/api/v1/meetings/retention/run', { body: { dryRun } })),
    onSuccess: (r) => {
      if (!r.dryRun) void qc.invalidateQueries({ queryKey: meetingKeys.deletions });
    },
  });
}
